/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import {
  BETA_TESTING,
  CONTACT_SUPPORT_LINK,
  SUPPORT_EMAIL,
} from '../../constants';
import Instance from '../Instance';
import Loader from '../Loader';
import ErrorMessageWrapper from '../ErrorMessageWrapper';
import Credentials from '../Credentials';
import NewClusterDialog from './NewClusterDialog';
import {
  networkWithErrorConvert,
  clustersKey,
  useClusterDeletion,
  useClusterCreation,
  useQuery,
  useMutation,
  queryClient,
} from '../../api';
import _ from 'lodash';

const serverCapacityReachedMsg =
  "Can't create a server. AllegroGraph Cloud reached the limit of servers";

function InstancesTitle({
  createCluster,
  disabled,
  maxServers,
  userCapacityReached,
  totalCapacityReached,
}) {
  const [clusterDialogOpen, setClusterDialogOpen] = React.useState(false);
  const open = () => {
    setClusterDialogOpen(true);
  };
  const close = () => {
    setClusterDialogOpen(false);
  };
  const userCapacityReachedMsg = `You have reached the maximum number of servers (${maxServers} ${
    maxServers === 1 ? 'server' : 'servers'
  }) for your tier`;
  return (
    <>
      <NewClusterDialog
        isOpen={clusterDialogOpen}
        close={close}
        createCluster={createCluster}
      />
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h5">AllegroGraph servers</Typography>
        <Tooltip
          data-cy="new-instance-button"
          title={
            userCapacityReached
              ? userCapacityReachedMsg
              : totalCapacityReached
              ? serverCapacityReachedMsg
              : 'Add new AllegroGraph server'
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={disabled}
              onClick={BETA_TESTING ? () => createCluster({}) : open}
            >
              Create server
            </Button>
          </span>
        </Tooltip>
        <Typography color="text.disabled">
          {userCapacityReached ? userCapacityReachedMsg : ''}
        </Typography>
      </Stack>
    </>
  );
}

function ErrorLoadingInstances({ message }) {
  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ErrorMessageWrapper
        containerStyles={{
          gap: 2,
          pt: 0,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">{message}</Typography>
      </ErrorMessageWrapper>
      <Typography variant="bod1">
        Please email{' '}
        <Link href={CONTACT_SUPPORT_LINK} target="_blank" underline="hover">
          {SUPPORT_EMAIL}
        </Link>{' '}
        or try again later
      </Typography>
    </Container>
  );
}

function useClusterLoad() {
  return useQuery(
    clustersKey,
    async () =>
      (await networkWithErrorConvert({ url: '/v1/clusters' })).clusters
  );
}

function useCapacityCheck() {
  return useQuery(['user-capacity'], () =>
    networkWithErrorConvert({ url: '/v1/user/available-capacity' })
  );
}

export default function Instances() {
  const { data: clusters, isLoading, isError } = useClusterLoad();
  const {
    data: newClusterInfo,
    isLoading: isClusterCreationInProgress,
    reset,
    mutate: createCluster,
  } = useClusterCreation(
    (bodyParams) => ({
      method: 'post',
      data: bodyParams,
      url: '/v1/cluster',
    }),
    (clustersList) =>
      clustersList.concat({
        id: `fake-id-${_.random(10000)}`,
        fake: true,
      })
  );
  const { mutate: deleteCluster, isLoading: isClusterDeletionInProgress } =
    useClusterDeletion((id) => ({
      method: 'delete',
      url: '/v1/cluster',
      params: { id },
    }));

  const {
    data,
    isLoading: isLoadingCapacityCheck,
    isFetching: isFetchingCapacityCheck,
    isError: isErrorOnCapacityCheck,
  } = useCapacityCheck();
  const {
    userMaxCapacity = 0,
    userCapacityReached = true,
    userCanCreateCluster = false,
    willBeNotified = false,
    totalCapacityReached = true,
  } = data || {};
  const notifyAvailabilityMutation = useMutation(
    () =>
      networkWithErrorConvert({
        url: '/v1/notify-availability',
        method: 'post',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-capacity']);
      },
    }
  );

  if (isError) {
    return (
      <ErrorLoadingInstances message="An error occurred when loading servers" />
    );
  }
  if (isErrorOnCapacityCheck) {
    return (
      <ErrorLoadingInstances message="An error on loading user's resources" />
    );
  }
  if (isLoading || isLoadingCapacityCheck) {
    return <Loader />;
  }
  const disabled =
    isClusterCreationInProgress ||
    isClusterDeletionInProgress ||
    isFetchingCapacityCheck;
  const disabledDueToUserLimit =
    !userCanCreateCluster || totalCapacityReached || userCapacityReached;
  return (
    <Stack spacing={2}>
      <Credentials newCluster={newClusterInfo} resetCredentials={reset} />
      <InstancesTitle
        maxServers={userMaxCapacity}
        disabled={disabled || disabledDueToUserLimit}
        userCapacityReached={userCapacityReached}
        totalCapacityReached={totalCapacityReached}
        createCluster={createCluster}
      />
      {!userCapacityReached && totalCapacityReached ? (
        <>
          <Typography align="left" sx={{ pt: 2, fontSize: '1.2rem' }}>
            {serverCapacityReachedMsg}.{' '}
            {willBeNotified
              ? "You'll get an email notification when a server can be created."
              : 'If you want to get an email notification when a server can be created please click "NOTIFY" button below.'}
          </Typography>
          <Button
            data-cy="notify-button"
            sx={{ width: 'fit-content' }}
            variant="contained"
            onClick={notifyAvailabilityMutation.mutate}
            disabled={
              isFetchingCapacityCheck ||
              notifyAvailabilityMutation.isLoading ||
              willBeNotified
            }
          >
            Notify
          </Button>
        </>
      ) : clusters.length === 0 ? (
        <Typography align="left" sx={{ pt: 2, fontSize: '1.2rem' }}>
          No servers yet. Click the button above to create a new one.
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {clusters.map((instanceInfo) => {
          return (
            <Instance
              key={instanceInfo.id}
              disabled={disabled}
              deleteCluster={deleteCluster}
              {...instanceInfo}
            />
          );
        })}
      </Box>
    </Stack>
  );
}
